import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import type { LadderAnswer } from '../Ladder/claims';

/** What the scope chip says (#767): "Auto · used X" when Stroma picked, the pick's name when the asker did. Inside an
 *  objective (#766) the pick is said the same way, and "· from objective" when it is still the objective's own. A
 *  Refresh (#762) that ran outside the pick says "X · from the earlier answer". */
export function scopeLabel(scope: NonNullable<LadderAnswer['scope']>, names: Record<string, string>, objectivePick?: string | null,
                           yours: boolean = false): string {
  const used = scope.used.map((u) => names[u.id] || u.name);
  // a Refresh (#762, owner decision A) ran in the earlier analysis's perspective: said so whenever that is not the pick;
  // so does a question about an earlier answer of the thread (#1807 F2: "from your earlier answer")
  const ran = scope.from_earlier ? scope.used[0] : undefined;
  if (ran && (scope.pick === 'auto' || scope.pick !== ran.id)) {
    return `${names[ran.id] || ran.name} · from ${yours ? 'your' : 'the'} earlier answer`;
  }
  const label = scope.pick === 'auto' ? (used.length ? `Auto · used ${used.join(', ')}` : 'Auto')
    : names[scope.pick] || scope.used.find((u) => u.id === scope.pick)?.name || scope.pick;
  return scope.source === 'objective' && objectivePick === scope.pick ? `${label} · from objective` : label;
}

/**
 * The perspective an answer honoured (#767, #749 "Choosing a perspective"), beside the route line. Change re-asks
 * the question in another perspective and makes it the thread's pick — the asker's own action. A catalog answer for
 * a picked perspective offers "Show across all perspectives": the same question under Auto, this question only.
 * An answer on a route that ignores the pick (knowledge, help, decline) has no scope and shows nothing.
 */
/** The perspective an answer honoured, as the scope chip says it, and its Change (the chip's, and the answer's More
 *  menu's). Null on a route that ignores the pick. */
export function useScopeChange(turn: LandingAgentTurn) {
  const ladder = turn.result?.ladder as LadderAnswer | null | undefined;
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  // the objective's own perspective (#766), when the answer was asked inside one the viewer can open
  const objectivePick = useVoyagerStore((s) => {
    const agent = ladder?.objective ? s.agents.find((a) => a.id === ladder.objective?.id) : null;
    return agent ? agent.objective.perspective_id || 'auto' : null;
  });
  const reaskInPerspective = useVoyagerStore((s) => s.reaskInPerspective);
  const scope = ladder?.scope;
  if (!scope) return null;
  const names = Object.fromEntries(perspectives.map((p) => [p.id, p.name || p.id]));
  const reask = turn.askedRequest;
  return {
    scope,
    label: scopeLabel(scope, names, objectivePick, !!ladder?.earlier),
    /** the other perspectives a Change offers; none without the turn's own request */
    choices: reask ? [{ id: 'auto', name: 'Auto' }, ...perspectives.map((p) => ({ id: p.id, name: p.name || p.id }))]
      .filter((c) => c.id !== scope.pick) : [],
    change: (id: string) => reaskInPerspective(turn, id, 'thread'),
    showAcross: reask && scope.source === 'user' && ladder?.route?.route === 'catalog'
      ? () => reaskInPerspective(turn, 'auto', 'question') : null,
  };
}

export default function ScopeChip({ turn }: { turn: LandingAgentTurn }) {
  const scoping = useScopeChange(turn);
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  if (!scoping) return null;
  const reask = turn.askedRequest;
  const change = (id: string) => { setAnchor(null); scoping.change(id); };
  const choices = scoping.choices;
  const textSx = { minWidth: 0, px: 0.75, textTransform: 'none', color: 'var(--accent)' } as const;
  return (
    <Box data-testid="scope-line" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
      <Chip size="small" variant="outlined" data-testid="scope-chip" label={scoping.label}
            sx={{ borderRadius: 99, borderColor: 'var(--line)', color: 'var(--ink-2)', bgcolor: 'var(--surface)' }} />
      {reask && <Button size="small" variant="text" aria-haspopup="menu" data-testid="scope-change"
                        onClick={(e) => setAnchor(e.currentTarget)} sx={textSx}>Change</Button>}
      {scoping.showAcross && (
        <Button size="small" variant="text" data-testid="scope-show-across" sx={textSx}
                onClick={scoping.showAcross}>Show across all perspectives</Button>
      )}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {choices.map((c) => <MenuItem key={c.id} onClick={() => change(c.id)}>{c.name}</MenuItem>)}
      </Menu>
    </Box>
  );
}
